import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Tooltip } from '@material-ui/core';
import dayjs from 'dayjs';
import { IoIosWarning } from 'react-icons/io';
import clsx from 'clsx';
import _ from 'lodash';
import formatDuration from '../../../../../helpers/formatters/formatDuration';
import Label from '../../../../Label';
// import calculateDuration from '../../../../../helpers/formatters/calculateDuration';
import checkDoFlightHaveCabinDowngrade from '../../../../../helpers/flightCabins/checkDoFlightHaveCabinDowngrade';

const useStyles = makeStyles((theme) => ({
  arrow: {
    color: theme.palette.common.black,
  },
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    borderStyle: 'solid',
    borderWidth: '2px',
    fontSize: '12px',
  },
}));

const OneWayFlightDetails = ({
  flights,
  doShowPrice,
  preferredCabin,
  avoidCabinDowngrade,
  highestRankedCabin,
  doShowCompactView,
}) => {
  const classes = useStyles();

  const doFlightHaveCabinDowngrade = useCallback(
    (flight) =>
      checkDoFlightHaveCabinDowngrade(
        preferredCabin,
        highestRankedCabin,
        flight.originalCabinClass,
      ),
    [highestRankedCabin, preferredCabin],
  );

  const isAfterDepartureDate = (departDateTime, arriveDateTime) => {
    const departDate = dayjs(departDateTime).startOf('day');
    const arriveDate = dayjs(arriveDateTime).startOf('day');
    const isDifferentDates = !departDate.isSame(arriveDate, 'day');
    return isDifferentDates && arriveDate.isAfter(departDate);
  };

  const shouldShowDepartDateTime = (currentSegmentIndex, allSegments) =>
    currentSegmentIndex > 0 &&
    !dayjs(allSegments[currentSegmentIndex].departDateTime).isSame(
      allSegments[currentSegmentIndex - 1].departDateTime,
      'day',
    );

  // const formatFlightTimes = (flight, currentSegmentIndex, allFlights) => {
  //   if (currentSegmentIndex > 0) {
  //     const prevFlight = allFlights[currentSegmentIndex - 1];
  //     const currentDate = dayjs(flight.departDateTime).startOf('day');
  //     const previousDate = dayjs(prevFlight.departDateTime).startOf('day');
  //     if (!currentDate.isSame(previousDate, 'day')) {
  //       return `+${currentDate.diff(previousDate, 'day')}`;
  //     }
  //   }
  //   return undefined;
  // };
  return (
    <>
      <div>Departs on {dayjs(flights[0].departDateTime).format('dddd, MMMM D, YYYY')}</div>
      {flights.map((flight, index) => (
        <div
          key={`${flight.originAirportFullName}${flight.carrierFullName}`}
          className={clsx('grid', {
            'grid-cols-10': !doShowCompactView,
            'grid-cols-7': doShowCompactView,
            'mt-3': index > 0,
          })}
        >
          {flight.layoverDuration > 0 && (
            <div className="col-span-10 border-t-2 border-b-2 mb-3 py-1">
              {formatDuration(flight.layoverDuration)} layover
              {' • '}
              {index > 0 &&
                flights[index - 1] &&
                flights[index - 1].flightNumber === flight.flightNumber &&
                'No plane change'}
              {index > 0 &&
                flights[index - 1] &&
                flights[index - 1].flightNumber !== flight.flightNumber &&
                flights[index - 1].destinationAirportCode === flight.originAirportCode && (
                  <span>
                    Change plane in
                    {` ${flight.originAirportFullName} (${flight.originAirportCode}) `}
                    {flights[index - 1].destinationAirportTerminalId !==
                      flight.originAirportTerminalId &&
                      // To avoid 'Transfer from terminal 5 to null' or vice versa.
                      // because sometimes we don't have terminal id for destination or origin
                      flights[index - 1].destinationAirportTerminalId &&
                      flight.originAirportTerminalId && (
                        <>
                          {' • '}
                          <Tooltip
                            arrow
                            classes={classes}
                            title={`Transfer from Terminal ${flights[index - 1].destinationAirportTerminalId} to Terminal ${flight.originAirportTerminalId} at ${flight.originAirportCode}`}
                            placement="top"
                            enterTouchDelay={0}
                          >
                            <span className="border-b border-dotted border-current text-red-500">
                              Terminal change
                              <IoIosWarning className="inline-block text-lg -mt-1 ml-1 mr-1" />
                            </span>
                          </Tooltip>
                        </>
                      )}
                  </span>
                )}
              {index > 0 &&
                flights[index - 1] &&
                flights[index - 1].flightNumber !== flight.flightNumber &&
                flights[index - 1].destinationAirportCode !== flight.originAirportCode && (
                  <span className="text-red-500">
                    Transfer from{' '}
                    <Tooltip
                      arrow
                      classes={classes}
                      title={
                        flights[index - 1].destinationAirportFullName ||
                        flights[index - 1].destinationAirportCode
                      }
                      placement="top"
                      enterTouchDelay={0}
                    >
                      <span className="border-b border-dotted border-current">
                        {flights[index - 1].destinationAirportCode}
                      </span>
                    </Tooltip>{' '}
                    to{' '}
                    <Tooltip
                      arrow
                      classes={classes}
                      title={flight.originAirportFullName || flight.originAirportCode}
                      placement="top"
                      enterTouchDelay={0}
                    >
                      <span className="border-b border-dotted border-current">
                        {flight.originAirportCode}
                      </span>
                    </Tooltip>
                    <IoIosWarning className="inline-block text-lg text-red-500 -mt-1 ml-1" />
                  </span>
                )}
            </div>
          )}
          <div
            className={clsx({
              'px-2 py-2 rounded-md bg-red-100': doFlightHaveCabinDowngrade(flight),
              'col-span-10 grid grid-cols-10': !doShowCompactView,
              'col-span-7 grid grid-cols-7': doShowCompactView,
            })}
          >
            <div className="col-span-7">
              <div>
                {flight.leaving} - {flight.arriving}
                {/* {calculateDuration(flight.departDateTime, flight.arriveDateTime) && (
                  <Tooltip arrow classes={classes}
                  title={`Arrive on ${dayjs(flight.arriveDateTime).format('dddd, MMMM D')}`}
                  placement="top" enterTouchDelay={0}>
                    <sup className="text-red-500 ml-1 font-bold">
                      {calculateDuration(flight.departDateTime, flight.arriveDateTime)}
                    </sup>
                  </Tooltip>
                )} */}
                {doShowCompactView && <div>Duration: {formatDuration(flight.duration)}</div>}
                {shouldShowDepartDateTime(index, flights) && (
                  <div className="text-red-500 text-xs">
                    {`Departs ${dayjs(flight.departDateTime).format('ddd, MMM D')}`}
                  </div>
                )}
                {isAfterDepartureDate(flight.departDateTime, flight.arriveDateTime, index) && (
                  <div className="text-red-500 text-xs">
                    {`Arrives ${dayjs(flight.arriveDateTime).format('ddd, MMM D')}`}
                  </div>
                )}
              </div>
              {flight.originAirportFullName} ({flight.originAirportCode}) -{' '}
              {flight.destinationAirportFullName} ({flight.destinationAirportCode})
              <div className="text-gray-600 flex items-center gap-1">
                <span>{flight.carrierFullName}</span>

                {flight.aircraftName && (
                  <>
                    <span className="flex items-center justify-center h-[1em] w-[1em]">·</span>
                    <span>{flight.aircraftName}</span>
                  </>
                )}
                <span className="flex items-center justify-center h-[1em] w-[1em]">·</span>
                <span>
                  <Tooltip
                    arrow
                    classes={classes}
                    title={flight.carrierFullName}
                    placement="top"
                    enterTouchDelay={0}
                  >
                    <span className="border-b border-dotted border-current">
                      {flight.carrierCode}
                    </span>
                  </Tooltip>{' '}
                  {flight.flightNumber}
                </span>
              </div>
              {(flight.operatingAirlineCompanyName ||
                (flight.operatingAirline &&
                  (flight.operatingAirline.companyName ||
                    (flight.operatingAirline.code &&
                      flight.operatingAirline.code !== flight.carrierCode)))) && (
                <div className="text-gray-600">
                  Operated by{' '}
                  {flight.operatingAirlineCompanyName ||
                    flight.operatingAirline.companyName ||
                    flight.operatingAirline.name}
                  {flight.operatingAirline?.flightNumber &&
                    ` (${flight.operatingAirline.code} ${flight.operatingAirline.flightNumber})`}
                </div>
              )}
            </div>
            {!doShowCompactView && (
              <div className="text-right col-span-3">
                <div>{formatDuration(flight.duration)}</div>
                <div>
                  {flight.fareBrandName ? (
                    <div className="capitalize">
                      {`${flight.fareBrandName.toLowerCase()} (${flight.fareClass})`}
                      <div>
                        {doFlightHaveCabinDowngrade(flight) ? (
                          <LowerCabinWarning avoidCabinDowngrade={avoidCabinDowngrade} />
                        ) : null}
                        {`${flight.cabinClassName} Cabin`}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {doFlightHaveCabinDowngrade(flight) ? (
                        <LowerCabinWarning avoidCabinDowngrade={avoidCabinDowngrade} />
                      ) : null}
                      {`${`${flight.cabinClassName} Cabin`} (${flight.fareClass})`}
                    </div>
                  )}
                  {doShowPrice ? (
                    <Label
                      text={_.capitalize(flight.refundabilityLabel)}
                      level={flight.isRefundable ? 'success' : 'danger'}
                      descriptionOnHover={flight.selectedFareRefundPolicy}
                      tooltipPlacement="top"
                      showInfoIndicator
                    />
                  ) : (
                    ''
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

const LowerCabinWarning = ({ avoidCabinDowngrade }) => {
  const classes = useStyles();
  let title = <span>The flight segment has a lower cabin class than requested.</span>;
  if (!avoidCabinDowngrade) {
    title = (
      <span>
        The flight segment has a lower cabin class than requested.
        <br />
        Please adjust your cabin class preferences to prevent lower cabin allocation.{' '}
        <b>Use the &quot;Avoid cabin downgrade&quot; preference.</b>
      </span>
    );
  }
  return (
    <Tooltip arrow classes={classes} title={title} placement="top" enterTouchDelay={0}>
      <span>
        <IoIosWarning className="inline-block text-lg text-red-500 -mt-1 mr-1" />
      </span>
    </Tooltip>
  );
};

OneWayFlightDetails.propTypes = {
  flights: PropTypes.arrayOf(
    PropTypes.shape({
      duration: PropTypes.number.isRequired,
      departDateTime: PropTypes.string.isRequired,
      arriveDateTime: PropTypes.string.isRequired,
      cabinClassName: PropTypes.string.isRequired,
      originAirportCode: PropTypes.string.isRequired,
      originAirportFullName: PropTypes.string.isRequired,
      destinationAirportCode: PropTypes.string.isRequired,
      destinationAirportFullName: PropTypes.string.isRequired,
      destinationAirportTerminalId: PropTypes.string,
      originAirportTerminalId: PropTypes.string,
      operatingAirlineCompanyName: PropTypes.string,
      flightNumber: PropTypes.string.isRequired,
      carrierCode: PropTypes.string.isRequired,
      carrierFullName: PropTypes.string.isRequired,
      isRefundable: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  doShowPrice: PropTypes.bool,
  preferredCabin: PropTypes.string,
  avoidCabinDowngrade: PropTypes.bool.isRequired,
  highestRankedCabin: PropTypes.string,
  doShowCompactView: PropTypes.bool,
};

OneWayFlightDetails.defaultProps = {
  doShowPrice: true,
  preferredCabin: null,
  highestRankedCabin: '',
  doShowCompactView: false,
};

LowerCabinWarning.propTypes = {
  avoidCabinDowngrade: PropTypes.bool.isRequired,
};

export default React.memo(OneWayFlightDetails);
